<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount } from "vue";
import { useRoute } from "vue-router";
import { useAuthStore } from "../stores/authStore";
import ramLogo from "../assets/images/ram_logo.svg";
import Footer from "../components/Footer.vue";
import { IconArrowRight } from "@tabler/icons-vue";
import { hideChatWidget } from "../utils/hideChatWidget";
import { showChatWidget } from "../utils/showChatWidget";
import { setupCheckAuth } from "../utils/setupCheckAuth";

const authStore = useAuthStore();
const route = useRoute();

const reason = computed(() => route.query.reason as string | undefined);

const heading = computed(() => {
  switch (reason.value) {
    case "inactivity":
      return "Session Expired";
    case "logout":
      return "You've Logged Out";
    default:
      return "Welcome to App.RAM";
  }
});

const subtext = computed(() => {
  switch (reason.value) {
    case "inactivity":
      return "Your session on App.RAM has expired due to inactivity. Click Login to start a new session.";
    case "logout":
      return "You have successfully logged out of App.RAM. Click Login to start a new session.";
    default:
      return "Login to get started.";
  }
});

onMounted(() => {
  localStorage.removeItem("mfaState");
  setupCheckAuth();
  hideChatWidget();
});

onBeforeUnmount(() => {
  showChatWidget();
});

function handleLogin() {
  authStore.login();
}
</script>

<template>
  <div class="flex h-full w-full flex-col bg-white font-inter">
    <div class="flex flex-1 items-center justify-center">
      <div class="w-full max-w-lg px-8 sm:px-12 text-center">
        <div class="mb-10 flex items-center justify-center gap-3">
          <img :src="ramLogo" alt="RAM Logo" class="h-12" />
          <span class="text-2xl font-bold text-gray-900 font-rambla"
            >App.RAM</span
          >
        </div>

        <h1
          class="text-3xl font-bold tracking-tight text-gray-900 whitespace-nowrap sm:text-4xl"
        >
          {{ heading }}
        </h1>
        <p class="mt-2 text-base text-gray-500">{{ subtext }}</p>
        <div class="mt-10">
          <button
            @click="handleLogin"
            class="group flex w-full items-center justify-center gap-2.5 rounded-xl bg-ram-yellow px-6 py-3.5 text-base font-semibold text-white shadow-lg shadow-ram-yellow/20 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-ram-yellow/30 focus:outline-none focus:ring-2 focus:ring-ram-yellow focus:ring-offset-2 active:translate-y-0 active:scale-[0.99]"
          >
            Login
            <IconArrowRight
              class="h-5 w-5 transition-transform duration-200 group-hover:translate-x-0.5"
            />
          </button>
        </div>
      </div>
    </div>

    <div class="px-8 pb-6">
      <Footer />
    </div>
  </div>
</template>
